import { DollarSign } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import {
	PunchupDialogue,
	DialogIcon,
	DialogTitle,
	ButtonGroup,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupDetailText } from 'features/common/components/Punchup/composites/content/PunchupDetailText';
import styles from './modals.module.css';

interface RefundModalProps {
	amount: number;
	reason: string;
	onReasonChange: (reason: string) => void;
	onConfirm: () => void;
	onClose: () => void;
	isLoading: boolean;
}

export const RefundModal: FC<RefundModalProps> = ({
	amount,
	reason,
	onReasonChange,
	onConfirm,
	onClose,
	isLoading,
}) => {
	const { t } = useTranslation();
	return (
		<PunchupDialogue maxWidth={400} isLoading={isLoading} isDestructive onClose={onClose}>
			<DialogIcon variant="destructive">
				<DollarSign size={24} />
			</DialogIcon>
			<DialogTitle>{t('attendee-actions-refund-ticket', 'Refund Ticket')}</DialogTitle>
			<PunchupDetailText
				main={t(
					'attendee-actions-refund-main',
					`Issue a full refund of $${amount.toFixed(2)} to the buyer's original payment method.`
				)}
				secondary={t(
					'attendee-actions-refund-secondary',
					'The full ticket price is returned to the buyer. Payment processing fees charged by your provider are typically non-recoverable and will not be included in the refund. This action cannot be undone.'
				)}
				collapsible
			/>
			<textarea
				className={styles.textarea}
				placeholder={t('attendee-actions-refund-reason', 'Note for your records (optional)')}
				value={reason}
				onChange={(e) => onReasonChange(e.target.value)}
			/>
			<ButtonGroup>
				<PunchupButton variant="secondary" text={t('cancel', 'Cancel')} onClick={onClose} disabled={isLoading} />
				<PunchupButton variant="destructive" text={t('confirm', 'Confirm')} onClick={onConfirm} disabled={isLoading} />
			</ButtonGroup>
		</PunchupDialogue>
	);
};
